/* eslint-disable no-shadow */

import type { GetterTree } from 'vuex';

type Event = any;
type Venue = any;

export interface State {
    events: {
        data: Event[];
        loading: boolean;
    };
    venues: {
        data: Venue[];
        loading: boolean;
    };
    selectedEventId: number | null;
    selectedVenueId: number | null;
    selectedAreaId: string | null;
    selectedSeatCategoryId: number | null;
    zoomToSeatOnTicketSelection: boolean;
}

export const getInitialState = (): State => ({
    events: {
        data: [],
        loading: false,
    },
    venues: {
        data: [],
        loading: false,
    },
    selectedEventId: null,
    selectedVenueId: null,
    selectedAreaId: null,
    selectedSeatCategoryId: null,
    zoomToSeatOnTicketSelection: false,
});

export interface GettersValues {
    events: Event[];
    venues: Venue[];
    loadingEvents: boolean;
    loadingVenues: boolean;
    selectedEvent: Event | undefined;
    selectedVenue: Venue | undefined;
}

export type Getters = GetterTree<State, State> &
    Record<
        keyof GettersValues,
        (
            state: State,
            getters: GettersValues,
            rootState: any,
            rootGetters: any
        ) => GettersValues[keyof GettersValues]
    >;

const getters: Getters = {
    events(state) {
        return state.events.data;
    },
    venues(state) {
        return state.venues.data;
    },
    loadingEvents(state) {
        return state.events.loading;
    },
    loadingVenues(state) {
        return state.venues.loading;
    },
    selectedEvent(state, getters) {
        return getters.events.find((event) => event.id === state.selectedEventId);
    },
    selectedVenue(state, getters) {
        return getters.venues.find((venue) => venue.id === state.selectedVenueId);
    },
};

interface ActionContext {
    state: State;
    commit: (mutation: string, payload?: any) => void;
    dispatch: (action: string, payload?: any) => Promise<any>;
    getters: GettersValues;
    rootState: any;
    rootGetters: any;
}

const actions = ({
    getEvents,
    getVenues,
}: {
    getEvents: (mainEventId: number) => Promise<Event[]>;
    getVenues: (mainEventId: number) => Promise<Venue[]>;
}) => ({
    async loadData({ dispatch }: ActionContext, { mainEventId }: { mainEventId: number }) {
        await Promise.all([
            dispatch('loadEvents', { mainEventId }),
            dispatch('loadVenues', { mainEventId }),
        ]);
    },
    async loadEvents({ commit }: ActionContext, { mainEventId }: { mainEventId: number }) {
        commit('setLoadingEvents', true);

        try {
            const events = await getEvents(mainEventId);

            commit('setEvents', events);
        } finally {
            commit('setLoadingEvents', false);
        }
    },
    async loadVenues({ commit }: ActionContext, { mainEventId }: { mainEventId: number }) {
        commit('setLoadingVenues', true);

        try {
            const venues = await getVenues(mainEventId);
            commit('setVenues', venues);
        } finally {
            commit('setLoadingVenues', false);
        }
    },
    selectEvent({ getters, commit }: ActionContext, { eventId }: { eventId: number | null }) {
        const event = getters.events.find((event) => event.id === eventId);

        if (!eventId) {
            commit('setSelectedEventId', null);
            return;
        }

        commit('setSelectedEventId', eventId);

        if (event.venue) {
            const venue = getters.venues.find((venue) => venue.id === event.venue.id);

            if (venue) {
                commit('setSelectedVenueId', venue.id);
            }
        }
    },
    selectVenue({ getters, commit }: ActionContext, { venueId }: { venueId: number | null }) {
        commit('setSelectedVenueId', venueId);

        if (getters.selectedEvent?.venue.id !== venueId) {
            commit('setSelectedEventId', null);
        }
    },
});

const mutations = {
    setEvents(state: State, events: Event[]) {
        state.events.data = events;
    },
    setVenues(state: State, venues: Venue[]) {
        state.venues.data = venues;
    },
    setLoadingEvents(state: State, loading: boolean) {
        state.events.loading = loading;
    },
    setLoadingVenues(state: State, loading: boolean) {
        state.venues.loading = loading;
    },
    setSelectedEventId(state: State, eventId: number | null) {
        state.selectedEventId = eventId;
    },
    setSelectedVenueId(state: State, eventId: number | null) {
        state.selectedVenueId = eventId;
    },
    setSelectedAreaId(state: State, areaId: string | null) {
        state.selectedAreaId = areaId;
    },
    setSelectedSeatCategoryId(state: State, seatCategoryId: number | null) {
        state.selectedSeatCategoryId = seatCategoryId;
    },
    setZoomToSeatOnTicketSelection(state: State, enabled: boolean) {
        state.zoomToSeatOnTicketSelection = enabled;
    },
};

export const pickerTopBarModuleShared = {
    getInitialState,
    getters,
    actions,
    mutations,
};
